<template>
  <div>
    <v-row>
      <v-col md="8" xs="12">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="form.title"
              name="title"
              :counter="65"
              :label="`${$t('ITEM.TITLE')} *`"
              :error-messages="errors.title"
              :readonly="readonly"
              :rules="getRulesByName('title')"
              @input="updateValue('title', $event)"
            />
          </v-col>
          <v-col cols="12">
            <v-card class="pa-4" outlined :loading="loading">
              <language-tabs
                ref="languageTabs"
                v-model="form.translations"
                :errors="errors.translations"
                #default="{ translation, index, language, currentLocale, on, ...other }"
                eager
              >
                <booking-resource-languages-form
                  :value="translation"
                  :language-id="language.id"
                  :locale="currentLocale"
                  :readonly="readonly"
                  v-bind="other"
                  v-on="on"
                  @input="updateValue(`translations[${index}]`, $event)"
                />
              </language-tabs>
            </v-card>
          </v-col>
          <v-col cols="12">
            <simple-gallery
              v-model="form.uploads"
              :readonly="readonly"
              :aspect-ratio="16 / 9"
            />
          </v-col>
          <v-col v-if="!readonly" cols="12">
            <auto-upload-file-input
              v-model="form.uploads"
              :label="$t('ITEM.FILES')"
              :url="`uploads/booking-resources`"
              :error-messages="errors.upload_ids"
              :rules="getRulesByName('uploads')"
              prepend-icon="mdi-file"
              counter="10"
              error-count="5"
              chips
              show-size
              destroyed
              multiple
              @input="form.upload_ids = $event"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col md="4" xs="12">
        <v-card outlined :loading="loading">
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="form.status_id"
                  name="status_id"
                  item-value="id"
                  item-text="name"
                  :items="statusesList"
                  :readonly="readonly"
                  :label="`${$t('ITEM.STATUS')} *`"
                  :error-messages="errors.status_id"
                  :rules="getRulesByName('status_id')"
                  @change="updateValue('status_id', $event)"
                />
              </v-col>

              <v-col cols="12">
                <v-checkbox
                  v-model="form.is_notify"
                  name="is_notify"
                  :label="$t('BOOKING.RESOURCES.RESERVATION_NOTIFY')"
                  :readonly="readonly"
                  :error-messages="errors.is_notify"
                  :rules="getRulesByName('is_notify')"
                  @change="
                    updateValue('is_notify', $event);
                    form.notification_emails = [];
                  "
                />
              </v-col>

              <v-col v-if="form.is_notify" cols="12">
                <v-combobox
                  v-model="form.emails"
                  class="pt-0"
                  :items="[]"
                  :readonly="readonly"
                  :error-messages="errors.emails"
                  small-chips
                  :clearable="!readonly"
                  multiple
                  outlined
                  :rules="getRulesByName('emails')"
                  :label="`${$t('PROFILE.EMAIL')} *`"
                >
                  <template
                    v-slot:selection="{ attrs, item, select, selected }"
                  >
                    <v-chip
                      v-bind="attrs"
                      :input-value="selected"
                      close
                      small
                      @click="select"
                      @click:close="
                        !readonly
                          ? form.emails.splice(form.emails.indexOf(item), 1)
                          : () => {}
                      "
                    >
                      <strong>{{ item }}</strong>
                    </v-chip>
                  </template>
                </v-combobox>
              </v-col>

              <v-col cols="6">
                <v-select
                  v-model="form.building_complex_id"
                  name="building_complex_id"
                  item-value="id"
                  item-text="name"
                  :items="buildingComplexesList"
                  :readonly="readonly"
                  :label="`${$t('BUILDING_COMPLEXES.ITEM')} *`"
                  :error-messages="errors.building_complex_id"
                  :rules="getRulesByName('building_complex_id')"
                  @change="changeBuildingComplex"
                />
              </v-col>

              <v-col cols="6">
                <v-select
                  v-model="form.building_id"
                  name="building_id"
                  item-value="id"
                  item-text="street"
                  :items="buildingsList"
                  :readonly="readonly"
                  :label="`${$t('BUILDINGS.ITEM')} *`"
                  :error-messages="errors.building_id"
                  :rules="getRulesByName('building_id')"
                  @change="updateValue('building_id', $event)"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="form.lock_provider_id"
                  name="lock_provider_id"
                  item-value="id"
                  item-text="name"
                  :items="lockProviders"
                  :readonly="readonly"
                  :label="$t('BOOKING.RESOURCES.LOCK_PROVIDER')"
                  :clearable="!readonly"
                  @click:clear="$nextTick(clearLockId)"
                />
              </v-col>

              <v-col cols="4">
                <v-text-field
                  v-model.number="form.lock_id"
                  name="lock_id"
                  type="number"
                  min="1"
                  :label="$t('BOOKING.RESOURCES.LOCK_ID')"
                  :readonly="readonly"
                  :error-messages="errors.lock_id"
                  :rules="getRulesByName('lock_id')"
                  :disabled="lockIdIsValid"
                  @input="updateValue('lock_id', $event)"
                />
              </v-col>

              <v-col cols="4">
                <v-select
                  v-model="form.symbol"
                  name="symbol"
                  item-value="id"
                  item-text="name"
                  :items="['♯', '✓']"
                  :readonly="readonly"
                  :label="$t('BOOKING.RESOURCES.KEY_SYMBOL')"
                  :clearable="!readonly"
                  :error-messages="errors.symbol"
                  :disabled="lockIdIsValid"
                  :rules="getRulesByName('symbol')"
                  @change="updateValue('symbol', $event)"
                />
              </v-col>
            </v-row>

            <v-row v-if="!lockIdIsValid">
              <v-col cols="12" class="pb-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.RESOURCES.ACCESS_KEY_AVAILABLE') }}
                </v-card-text>
              </v-col>
              <v-col cols="6" class="pt-0">
                <v-select
                  v-model="form.time_before_min"
                  name="time_before_min"
                  item-value="id"
                  item-text="name"
                  :items="stepTimeAllowance"
                  :readonly="readonly"
                  :label="`${$t('BOOKING.RESOURCES.ALLOWANCE_BEFORE')} *`"
                  :error-messages="errors.time_before_min"
                  :disabled="lockIdIsValid"
                  :rules="getRulesByName('time_before_min')"
                  @change="updateValue('time_before_min', $event)"
                />
              </v-col>

              <v-col cols="6" class="pt-0">
                <v-select
                  v-model="form.time_after_min"
                  name="time_after_min"
                  item-value="id"
                  item-text="name"
                  :items="stepTimeAllowance"
                  :readonly="readonly"
                  :label="`${$t('BOOKING.RESOURCES.ALLOWANCE_AFTER')} *`"
                  :error-messages="errors.time_after_min"
                  :disabled="lockIdIsValid"
                  :rules="getRulesByName('time_after_min')"
                  @change="updateValue('time_after_min', $event)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-divider></v-divider>
            </v-row>
            <v-row align="center">
              <v-col cols="12" class="pb-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.CAPACITY.NAME') }}:
                  <span class="font-weight-bold">{{ form.capacity }}</span>
                  <div v-if="errors.capacity">{{ errors.capacity }}</div>
                </v-card-text>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.CAPACITY.RESOURCE_RESERVATION_TYPE') }}:
                  <span class="font-weight-bold">
                    {{ getReservationType }}
                  </span>
                  <div v-if="errors.type_id">{{ errors.type_id }}</div>
                </v-card-text>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.CAPACITY.RESERVATION_RANGE') }}:
                </v-card-text>
              </v-col>

              <v-col cols="12">
                <v-row>
                  <v-col cols="4">
                    <v-text-field
                      v-model.number="form.reserve_min"
                      name="reserve_min"
                      type="number"
                      min="1"
                      :counter="2"
                      :counter-value="countNumber"
                      :label="$t('BOOKING.RESOURCES.MIN')"
                      :readonly="readonly"
                      :error-messages="errors.reserve_min"
                      :rules="getRulesByName('reserve_min')"
                      @input="updateValue('reserve_min', $event)"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-text-field
                      v-model.number="form.reserve_max"
                      name="reserve_max"
                      type="number"
                      :min="form.reserve_min"
                      :counter="3"
                      :counter-value="countNumber"
                      :label="$t('BOOKING.RESOURCES.MAX')"
                      :readonly="readonly"
                      :error-messages="errors.reserve_max"
                      :rules="getRulesByName('reserve_max')"
                      @input="updateValue('reserve_max', $event)"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-select
                      v-model="form.step"
                      name="type_id"
                      item-value="id"
                      item-text="name"
                      :items="[5, 10, 15, 30, 60]"
                      :readonly="readonly"
                      :label="$t('ITEM.STEP')"
                      :error-messages="errors.step"
                      :disabled="form.type_id === day_id"
                      :rules="getRulesByName('step')"
                      @change="updateValue('step', $event)"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-divider></v-divider>
            </v-row>

            <v-row align="baseline">
              <v-col cols="12" class="pb-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.CAPACITY.DAYS') }}:
                </v-card-text>
              </v-col>
              <v-col cols="12">
                <weekdays
                  v-model="form.days"
                  color="primary"
                  :readonly="readonly"
                  :error-messages="errors.days"
                  :rules="getRulesByName('days')"
                  @input="updateValue('days', $event)"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <v-card-text class="pa-0">
                  {{ $t('BOOKING.CAPACITY.TIME') }}:
                </v-card-text>
              </v-col>
              <v-col cols="6" class="pt-0">
                <v-text-field
                  v-if="readonly"
                  name="time_from"
                  :value="form.time_from"
                  :label="`${$t('ITEM.TIME_FROM')} *`"
                  prepend-icon="mdi-clock-time-four-outline"
                  :rules="getRulesByName('time_from')"
                  :error-messages="errors.time_from"
                  readonly
                />

                <v-menu
                  v-else
                  ref="timeFrom"
                  v-model="timeFrom"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="form.time_from"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.time_from"
                      name="time_from"
                      :label="`${$t('ITEM.TIME_FROM')} *`"
                      prepend-icon="mdi-clock-time-four-outline"
                      :clearable="!readonly"
                      readonly
                      :rules="getRulesByName('time_from')"
                      :error-messages="errors.time_from"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-if="timeFrom"
                    v-model="form.time_from"
                    :allowed-minutes="allowedStep"
                    :max="form.time_to"
                    :readonly="readonly"
                    format="24hr"
                    scrollable
                    @click:minute="$refs.timeFrom.save(form.time_from)"
                    @input="updateValue('time_from', $event)"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6" class="pt-0">
                <v-text-field
                  v-if="readonly"
                  name="time_to"
                  :value="form.time_to"
                  :label="`${$t('ITEM.TIME_TO')} *`"
                  prepend-icon="mdi-clock-time-four-outline"
                  :rules="getRulesByName('time_to')"
                  :error-messages="errors.time_to"
                  readonly
                />

                <v-menu
                  v-else
                  ref="timeTo"
                  v-model="timeTo"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="form.time_to"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.time_to"
                      name="time_to"
                      prepend-icon="mdi-clock-time-four-outline"
                      :label="`${$t('ITEM.TIME_TO')} *`"
                      :rules="getRulesByName('time_to')"
                      :error-messages="errors.time_to"
                      :clearable="!readonly"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-if="timeTo"
                    v-model="form.time_to"
                    :allowed-minutes="allowedStep"
                    format="24hr"
                    :min="form.time_from"
                    :readonly="readonly"
                    scrollable
                    @click:minute="$refs.timeTo.save(form.time_to)"
                    @input="updateValue('time_to', $event)"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6" class="py-0">
                <v-checkbox
                  v-model="form.mass_market"
                  name="mass_market"
                  :label="$t('BOOKING.RESOURCES.AVAILABLE_FOR_THE_MASS_MARKET')"
                  :readonly="readonly"
                  :error-messages="errors.mass_market"
                  :rules="getRulesByName('mass_market')"
                  @change="updateValue('mass_market', $event)"
                />
              </v-col>
              <v-col cols="6" class="py-0">
                <v-checkbox
                  v-model="form.is_available_booking_app"
                  name="is_available_booking_app"
                  :label="$t('BOOKING.RESOURCES.AVAILABLE_FOR_RESERVATION')"
                  :readonly="readonly"
                  :error-messages="errors.is_available_booking_app"
                  :rules="getRulesByName('is_available_booking_app')"
                  @change="updateValue('is_available_booking_app', $event)"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-subheader>{{ $t('ITEM.PRICE') }}</v-subheader>
              </v-col>
              <v-col cols="8">
                <money-input
                  v-model="form.price"
                  name="price"
                  :label="$t('ITEM.AMOUNT')"
                  :readonly="readonly"
                  :error-messages="errors.price"
                  :rules="getRulesByName('price')"
                  @input="updateValue('price', $event)"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <slot name="buttons" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapCacheActions } from 'vuex-cache';
import formEditable from '@/modules/_global/mixins/form-editable';
import LanguageTabs from '@/modules/_global/_components/LanguageTabs.vue';
import Weekdays from '@/modules/_global/_components/Weekdays.vue';
import SimpleGallery from '@/modules/_global/_components/SimpleGallery.vue';
import AutoUploadFileInput from '@/modules/_global/_components/AutouploadFileInput.vue';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import {
  emails,
  maxLength,
  maxLengthNumber,
  maxLengthUploadArray,
  notEmpty,
  notEmptyArray,
  notEmptyWithNull
} from '@/modules/_global/variables/rules';
import { DAY } from '../../common/types';
import BookingResourceLanguagesForm from './BookingResourceLanguagesForm.vue';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';

export default {
  name: 'BookingResourceForm',

  components: {
    MoneyInput,
    Weekdays,
    LanguageTabs,
    SimpleGallery,
    AutoUploadFileInput,
    BookingResourceLanguagesForm
  },

  mixins: [formEditable, i18nRefreshData],

  data() {
    return {
      form: {
        status_id: 1,
        type_id: DAY,
        building_complex_id: null,
        building_id: null,
        lock_id: null,
        lock_provider_id: null,
        time_before_min: 15,
        time_after_min: 15,
        reserve_min: 1,
        reserve_max: 1,
        time_from: null,
        time_to: null,
        mass_market: false,
        is_available_booking_app: true,
        price: 0,
        step: 5,
        days: [1, 2, 3, 4, 5, 6, 0],
        translations: [],
        uploads: [],
        upload_ids: [],
        is_notify: false
      },
      rules: {
        title: [notEmpty, maxLength(65)],
        status_id: [notEmpty],
        type_id: [notEmpty],
        building_complex_id: [notEmpty],
        building_id: [notEmpty],
        reserve_min: [notEmpty, maxLengthNumber(2)],
        reserve_max: [notEmpty, maxLengthNumber(3)],
        time_from: [notEmpty],
        time_to: [notEmpty],
        days: [notEmptyArray],
        uploads: [maxLengthUploadArray(10)],
        time_before_min: [notEmptyWithNull],
        time_after_min: [notEmptyWithNull],
        emails: emails
      },
      timeFrom: false,
      timeTo: false,
      typesList: [],
      statusesList: [],
      buildingsList: [],
      buildingComplexesList: [],
      day_id: DAY,
      lockProviders: [],
      stepTimeAllowance: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]
    };
  },

  computed: {
    ...mapGetters(['loading']),

    getReservationType() {
      return (this.typesList.find(el => el.id === this.form.type_id) || {})
        .name;
    },

    isCreated() {
      return !this.form.id;
    },

    lockIdIsValid() {
      return !this.form.lock_provider_id && !this.form.lock_id;
    }
  },

  watch: {
    'form.building_complex_id': 'getBuildings'
  },

  mounted() {
    this.refreshData();
  },

  methods: {
    ...mapCacheActions('booking_resources', [
      'getBookingResourcesStatuses',
      'getBookingResourcesTypes',
      'getFilteredBuildings',
      'getFilteredBuildingComplexes',
      'getLockProviders'
    ]),

    refreshData() {
      this.getBookingResourcesStatuses().then(({ data }) => {
        this.statusesList = data.data;
      });

      this.getBookingResourcesTypes().then(({ data }) => {
        this.typesList = data.data;
      });

      this.getFilteredBuildingComplexes().then(({ data }) => {
        this.buildingComplexesList = data.data;
      });

      this.getBuildings();

      this.getLockProviders().then(({ data }) => {
        this.lockProviders = data;
      });
    },

    getBuildings() {
      if (!this.form.building_complex_id) {
        return;
      }

      this.getFilteredBuildings({
        building_complex: [this.form.building_complex_id]
      }).then(({ data }) => {
        this.buildingsList = data.data;
      });
    },

    changeBuildingComplex(value) {
      this.updateValue('building_complex_id', value);
      this.form.building_id = null;
    },

    countNumber(value) {
      return Math.floor(value).toString().length;
    },

    clearLockId() {
      this.form.lock_id = null;
      this.errors.lock_id = null;
    },

    allowedStep: m => m % 5 === 0
  }
};
</script>
